// 个人 Key：中台数据、本人主页、私有应用和可选知识库共用一把 Key。
// 开通时网页替主人在内部空间建一把只开「检索资料」的 API Key 交给 studio；之后给每个人发 studio 自己的 Key（sk-lingzhi- 开头，只显示一次）。
// 分区是知识库的顶层文件夹，studio 每次现读；每篇都带审核标记和飞书原文链接。

import { request } from './client'
import type { OpenScope } from './hub/open'
import { createApiKey, revokeApiKey } from './settings'

const BASE = '/studio/api/open'
/** MCP 的地址（和网页同一个域名）。 */
export const mcpUrl = () => `${window.location.origin}/studio/api/mcp`

export interface OpenSection {
  name: string
  kbId: string
  /** 顶层文件夹；根目录零散的是空串。 */
  folder: string
  count: number
  /** 给 AI 看的一句话说明（整区都带的审核标记 + 两篇例子）。 */
  about: string
  /** 整区都带的审核标记：未审核、派生整理，未经人审、草稿。 */
  labels: string[]
  /** 新发的 Key 默认开放它（派生整理（未经人审）、收集区、个人文件夹、零散的默认不开）。 */
  defaultOpen: boolean
}

export interface OpenKey {
  id: string
  name: string
  agentName?: string
  clientLabel?: string
  ownerId?: string
  agentId?: string
  prefix: string
  sections: string[]
  fullText: string[]
  research: boolean
  scopes?: OpenScope[]
  perHour: number
  createdAt: string
  expiresAt?: string
  revokedAt?: string
  lastUsedAt?: string
  calls24h: number
}

export interface OpenStatus {
  enabled: boolean
  knowledgeEnabled?: boolean
  /** 只开放这个空间（内部空间 10000）；网页不在这个空间时开通不了。 */
  tenant: string
  ownerName?: string
  kbIds?: string[]
  upstreamKeyId?: number | string
  since?: string
  sections?: OpenSection[]
  keys: OpenKey[]
  error?: string
}

export interface OpenCall {
  at: string
  tool: string
  /** 兼容旧版本；网页不呈现任何请求正文。 */
  query?: string
  count: number
  ms: number
  ok: boolean
  error?: string
}

export interface NewOpenKey {
  name: string
  sections: string[]
  fullText: string[]
  research: boolean
  scopes?: OpenScope[]
  perHour?: number
  expiresInDays?: number
}

const data = <T>(p: Promise<unknown>) => p.then((r) => (r as { data: T }).data)

export const getOpen = () => data<OpenStatus>(request(BASE))

/**
 * 开通：先在内部空间建一把只读 Key（只开检索），再交给 studio；studio 用不了就把刚建的吊销掉。
 * Key 不限库：知识库（WeKnora v0.8.0）不让限了库的 Key 按篇检索（按分区放行要靠它），开放哪些库由 kbIds 交给 studio 把关。
 */
export async function enableOpen(tenant: string | number, kbIds: string[], ownerName: string): Promise<OpenStatus> {
  const k = await createApiKey(tenant, { name: '对外接入（lingzhi-studio 专用，别删）', capabilities: ['retrieve'], knowledge_base_ids: [] })
  try {
    return await data<OpenStatus>(request(`${BASE}/enable`, { body: { apiKey: k.token, kbIds, ownerName, upstreamKeyId: k.id } }))
  } catch (e) {
    await revokeApiKey(tenant, k.id).catch(() => undefined)
    throw e
  }
}

/** 停用：发出去的 Key 全部失效，知识库里那把只读 Key 也吊销。 */
export async function disableOpen(tenant: string | number): Promise<void> {
  const r = await data<{ upstreamKeyId?: number | string }>(request(`${BASE}/disable`, { body: {} }))
  if (r.upstreamKeyId !== undefined) await revokeApiKey(tenant, Number(r.upstreamKeyId)).catch(() => undefined)
}

export const renameOpen = (ownerName: string) => data<OpenStatus>(request(BASE, { method: 'PUT', body: { ownerName } }))
export const createOpenKey = (k: NewOpenKey) => data<{ key: OpenKey; token: string }>(request(`${BASE}/keys`, { body: k }))
export const updateOpenKey = (id: string, k: Partial<NewOpenKey>) => data<OpenKey>(request(`${BASE}/keys/${id}`, { method: 'PUT', body: k }))
export const revokeOpenKey = (id: string) => data<OpenKey>(request(`${BASE}/keys/${id}`, { method: 'DELETE' }))
/** 删掉一把已经吊销的 Key（连同调用记录）。 */
export const deleteOpenKey = (id: string) => request(`${BASE}/keys/${id}`, { method: 'DELETE', query: { purge: 1 } })
export const listOpenCalls = (id: string) => data<OpenCall[]>(request(`${BASE}/keys/${id}/calls`))

export const TOOL_LABEL: Record<string, string> = { connect: '连上', search: '检索', fetch: '读一篇', browse: '看目录', research: '深入研究', research_result: '取研究结果' }
